'use client';

import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAppSettings } from '@/hooks/useAppSettings';
import { colorForCategory } from '@/lib/colors';
import { formatMoney } from '@/lib/money';
import type { Category, Transaction } from '@/lib/types';

type PeriodKey = '7d' | '30d' | '6m' | '1y' | 'max';

type SeriesOption = {
  id: string;
  name: string;
  color: string;
  total: number;
};

type ChartRow = {
  key: string;
  label: string;
  fullLabel: string;
  [seriesId: string]: string | number | undefined;
};

type ChartInteraction = {
  activeLabel?: string | number;
  activePayload?: Array<{ payload?: ChartRow }>;
};

const PERIODS: Array<{ key: PeriodKey; label: string; days?: number; months?: number }> = [
  { key: '7d', label: '7 jours', days: 7 },
  { key: '30d', label: '30 jours', days: 30 },
  { key: '6m', label: '6 mois', months: 6 },
  { key: '1y', label: '1 an', months: 12 },
  { key: 'max', label: 'Max' },
];

function toDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function dayLabel(key: string) {
  const [year, month, day] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit' }).format(new Date(year, month - 1, day));
}

function fullDayLabel(key: string) {
  const [year, month, day] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }).format(
    new Date(year, month - 1, day)
  );
}

function getRangeKeys(period: (typeof PERIODS)[number], expenses: Transaction[]) {
  const today = new Date();
  let start = addDays(today, -((period.days ?? 30) - 1));

  if (period.months) {
    start = addDays(addMonths(today, -period.months), 1);
  }

  if (period.key === 'max' && expenses.length > 0) {
    const oldest = expenses.reduce((min, tx) => (tx.date < min ? tx.date : min), expenses[0].date);
    const [year, month, day] = oldest.split('-').map(Number);
    start = new Date(year, month - 1, day);
  }

  const keys: Array<{ key: string; label: string; fullLabel: string }> = [];
  for (let cursor = start; cursor <= today; cursor = addDays(cursor, 1)) {
    const key = toDateKey(cursor);
    keys.push({ key, label: dayLabel(key), fullLabel: fullDayLabel(key) });
  }
  return keys;
}

function getRowFromChartEvent(event: unknown, rows: ChartRow[]) {
  const chartEvent = event as ChartInteraction | null;
  const payloadRow = chartEvent?.activePayload?.[0]?.payload;
  if (payloadRow?.key) return payloadRow;

  const activeLabel = chartEvent?.activeLabel;
  if (activeLabel === undefined) return null;
  return rows.find((row) => row.label === String(activeLabel) || row.key === String(activeLabel)) ?? null;
}

export default function CategoryEvolutionChart({
  transactions,
  categories,
}: {
  transactions: Transaction[];
  categories: Category[];
}) {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey>('30d');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const { settings } = useAppSettings();
  const money = (value: number) => formatMoney(value, settings.currency, 0);

  const period = PERIODS.find((item) => item.key === selectedPeriod) ?? PERIODS[1];
  const expenses = useMemo(() => transactions.filter((tx) => tx.type === 'expense'), [transactions]);
  const categoryById = useMemo(() => new Map(categories.map((category) => [category.id, category])), [categories]);
  const categoryIds = useMemo(() => new Set(categories.map((category) => category.id)), [categories]);

  const rangeKeys = useMemo(() => getRangeKeys(period, expenses), [expenses, period]);
  const rangeKeySet = useMemo(() => new Set(rangeKeys.map((item) => item.key)), [rangeKeys]);

  const { chartData, options } = useMemo(() => {
    const totals = new Map<string, number>();
    const rows = rangeKeys.map<ChartRow>((item) => ({
      key: item.key,
      label: item.label,
      fullLabel: item.fullLabel,
    }));
    const rowByKey = new Map(rows.map((row) => [row.key, row]));

    categories.forEach((category) => {
      totals.set(category.id, 0);
      rows.forEach((row) => {
        row[category.id] = 0;
      });
    });

    let uncategorizedTotal = 0;
    rows.forEach((row) => {
      row.uncategorized = 0;
    });

    expenses.forEach((tx) => {
      const bucketKey = tx.date;
      if (!rangeKeySet.has(bucketKey)) return;

      const seriesId = tx.categoryId && categoryIds.has(tx.categoryId) ? tx.categoryId : 'uncategorized';
      const row = rowByKey.get(bucketKey);
      if (!row) return;

      row[seriesId] = Number(row[seriesId] ?? 0) + tx.amount;
      if (seriesId === 'uncategorized') {
        uncategorizedTotal += tx.amount;
      } else {
        totals.set(seriesId, (totals.get(seriesId) ?? 0) + tx.amount);
      }
    });

    const categoryOptions = categories
      .map<SeriesOption>((category, index) => ({
        id: category.id,
        name: category.name,
        color: colorForCategory(category, index, categories),
        total: totals.get(category.id) ?? 0,
      }))
      .filter((option) => option.total > 0);

    if (uncategorizedTotal > 0) {
      categoryOptions.push({
        id: 'uncategorized',
        name: 'Sans catégorie',
        color: '#94a3b8',
        total: uncategorizedTotal,
      });
    }

    return {
      chartData: rows,
      options: categoryOptions.sort((a, b) => b.total - a.total),
    };
  }, [categories, categoryIds, expenses, rangeKeys, rangeKeySet]);

  const optionIdsKey = options.map((option) => option.id).join('|');
  const suggestedIdsKey = options
    .slice(0, 3)
    .map((option) => option.id)
    .join('|');

  useEffect(() => {
    setSelectedIds((current) => {
      const validIds = new Set(options.map((option) => option.id));
      const stillValid = current.filter((id) => validIds.has(id));
      if (stillValid.length > 0) return stillValid;
      return options.slice(0, 3).map((option) => option.id);
    });
  }, [optionIdsKey, suggestedIdsKey, options]);

  useEffect(() => {
    setActiveKey((current) => {
      if (current && chartData.some((row) => row.key === current)) return current;
      return chartData.length > 0 ? chartData[chartData.length - 1].key : null;
    });
  }, [chartData]);

  const selectedOptions = options.filter((option) => selectedIds.includes(option.id));
  const selectedTotal = selectedOptions.reduce((sum, option) => sum + option.total, 0);
  const activeIndex = Math.max(
    0,
    chartData.findIndex((row) => row.key === activeKey)
  );
  const activeRow = chartData[activeIndex] ?? chartData[chartData.length - 1] ?? null;
  const activeTotal = activeRow
    ? selectedOptions.reduce((sum, option) => sum + Number(activeRow[option.id] ?? 0), 0)
    : 0;
  const activeBreakdown = activeRow
    ? selectedOptions.map((option) => ({
        ...option,
        amount: Number(activeRow[option.id] ?? 0),
      }))
    : [];
  const highlightedChartData = useMemo(
    () =>
      chartData.map((row, index) => {
        const nextRow: ChartRow = { ...row };
        selectedOptions.forEach((option) => {
          const value = Number(row[option.id] ?? 0);
          nextRow[`${option.id}__before`] = index <= activeIndex ? value : undefined;
          nextRow[`${option.id}__after`] = index >= activeIndex ? value : undefined;
        });
        return nextRow;
      }),
    [activeIndex, chartData, selectedOptions]
  );

  function toggleCategory(id: string) {
    setSelectedIds((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      return [...current, id];
    });
  }

  function selectChartPoint(event: unknown) {
    const row = getRowFromChartEvent(event, chartData);
    if (row) setActiveKey(row.key);
  }

  function selectPointerPoint(event: PointerEvent<HTMLDivElement>) {
    const container = chartContainerRef.current;
    if (!container || chartData.length === 0) return;

    const rect = container.getBoundingClientRect();
    const chartLeftOffset = 64;
    const chartRightOffset = 18;
    const usableWidth = Math.max(rect.width - chartLeftOffset - chartRightOffset, 1);
    const position = Math.min(Math.max(event.clientX - rect.left - chartLeftOffset, 0), usableWidth);
    const ratio = position / usableWidth;
    const index = Math.round(ratio * (chartData.length - 1));
    setActiveKey(chartData[index]?.key ?? null);
  }

  function startPointerSelection(event: PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    selectPointerPoint(event);
  }

  return (
    <section className="section-card no-print">
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="eyebrow">Évolution</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-100">Courbe par catégorie</h2>
          <p className="mt-2 text-sm text-slate-400">
            Compare les dépenses jour par jour sur la période choisie.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 min-[420px]:flex min-[420px]:flex-wrap">
          {PERIODS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setSelectedPeriod(item.key)}
              className={`rounded-2xl border px-3 py-2 text-sm font-semibold transition min-[420px]:rounded-full ${
                selectedPeriod === item.key
                  ? 'border-brand-400 bg-brand-500/15 text-brand-100'
                  : 'border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-600'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {options.length === 0 ? (
        <div className="card-muted text-sm">Aucune dépense disponible pour cette période.</div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-2 min-[420px]:flex min-[420px]:flex-wrap">
            {options.map((option) => {
              const active = selectedIds.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => toggleCategory(option.id)}
                  className={`flex min-h-11 items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-semibold transition min-[420px]:rounded-full ${
                    active
                      ? 'border-slate-500 bg-slate-800 text-slate-100'
                      : 'border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: option.color }} />
                  <span>{option.name}</span>
                  <span className="text-xs text-slate-500">{money(option.total)}</span>
                </button>
              );
            })}
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950 p-4">
            <div className="mb-4 grid gap-3 lg:grid-cols-[1fr_1.2fr]">
              <div>
                <p className="text-sm font-medium text-slate-400">Total sélectionné</p>
                <p className="text-2xl font-semibold text-slate-100">{money(selectedTotal)}</p>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedOptions.length} catégorie{selectedOptions.length > 1 ? 's' : ''} affichée
                  {selectedOptions.length > 1 ? 's' : ''}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-900/80 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-400">Point sélectionné</p>
                    <p className="mt-1 font-semibold text-slate-100">{activeRow?.fullLabel ?? 'Aucune donnée'}</p>
                  </div>
                  <p className="shrink-0 text-xl font-semibold text-slate-100">{money(activeTotal)}</p>
                </div>
                {activeBreakdown.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {activeBreakdown.map((item) => (
                      <span key={item.id} className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-2.5 py-1 text-xs font-semibold text-slate-300">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        {item.name}: {money(item.amount)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div
              ref={chartContainerRef}
              className="category-evolution-chart h-64 cursor-crosshair touch-none select-none outline-none min-[420px]:h-72 sm:h-80"
              onPointerDown={startPointerSelection}
              onPointerMove={selectPointerPoint}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  accessibilityLayer={false}
                  data={highlightedChartData}
                  margin={{ top: 12, right: 18, bottom: 10, left: 0 }}
                  onMouseMove={selectChartPoint}
                  onClick={selectChartPoint}
                >
                  <CartesianGrid stroke="#1e293b" strokeDasharray="4 4" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    minTickGap={18}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    tickFormatter={(value) => money(Number(value))}
                    width={64}
                  />
                  <Tooltip
                    content={() => null}
                    cursor={false}
                    isAnimationActive={false}
                  />
                  {selectedOptions.map((option) => (
                    <Line
                      key={`${option.id}-after`}
                      type="monotone"
                      dataKey={`${option.id}__after`}
                      stroke={option.color}
                      strokeWidth={3}
                      strokeOpacity={0.22}
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                  {selectedOptions.map((option) => (
                    <Line
                      key={`${option.id}-before`}
                      type="monotone"
                      dataKey={`${option.id}__before`}
                      stroke={option.color}
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                  {activeRow
                    ? selectedOptions.map((option) => (
                        <ReferenceDot
                          key={`${option.id}-active-dot`}
                          x={activeRow.label}
                          y={Number(activeRow[option.id] ?? 0)}
                          r={4}
                          fill={option.color}
                          stroke="#f8fafc"
                          strokeWidth={2}
                        />
                      ))
                    : null}
                </LineChart>
              </ResponsiveContainer>
            </div>

          </div>
        </div>
      )}
    </section>
  );
}
